import type { FpaVarianceCategory, VarianceReport } from './api';

/**
 * Boil a variance report down to the "so what": operating-profit actual vs plan
 * and the input-category drivers that explain the gap, sign-adjusted so a
 * positive `favorable` helps operating profit (revenue up = good; cost up = bad).
 * Shared by the answer-first band, the waterfall, and the Home overview.
 */

const DRIVER_CATS: FpaVarianceCategory[] = ['revenue', 'cost.variable', 'cost.salary', 'cost.fixed'];
const SIGN: Record<string, 1 | -1> = { revenue: 1, 'cost.variable': -1, 'cost.salary': -1, 'cost.fixed': -1 };
const LABEL: Record<string, string> = {
  revenue: 'Revenue', 'cost.variable': 'Variable cost', 'cost.salary': 'Salaries & wages', 'cost.fixed': 'Fixed cost',
};

export interface VarianceDriver {
  category: FpaVarianceCategory;
  label: string;
  actual: number;
  plan: number;
  /** sign-adjusted (actual − plan): + favourable to operating profit, − unfavourable. */
  favorable: number;
}
export interface VarianceSummary {
  opActual: number;
  opPlan: number;
  opDelta: number;           // opActual − opPlan (favourable when positive)
  drivers: VarianceDriver[]; // sorted by impact, largest first
  hasBaseline: boolean;
}

/** Summarize a variance report for one period (YYYY-MM), or the whole range when
 *  `period` is omitted. `baselineId` is the plan column to compare against. */
export function summarizeVariance(report: VarianceReport, baselineId: string | null, period?: string): VarianceSummary {
  const get = (cat: FpaVarianceCategory, side: 'actual' | 'plan'): number => {
    const row = report.rows.find((r) => r.category === cat);
    if (!row) return 0;
    if (period) {
      const c = row.cells.find((x) => x.period_month === period);
      return side === 'actual' ? (c?.actual ?? 0) : (baselineId ? (c?.baselines[baselineId] ?? 0) : 0);
    }
    return side === 'actual' ? row.total.actual : (baselineId ? (row.total.baselines[baselineId] ?? 0) : 0);
  };

  const drivers: VarianceDriver[] = DRIVER_CATS
    .map((cat) => {
      const actual = get(cat, 'actual');
      const plan = get(cat, 'plan');
      return { category: cat, label: LABEL[cat]!, actual, plan, favorable: SIGN[cat]! * (actual - plan) };
    })
    .filter((d) => d.actual !== 0 || d.plan !== 0)
    .sort((a, b) => Math.abs(b.favorable) - Math.abs(a.favorable));

  const opActual = get('operating_profit', 'actual');
  const opPlan = get('operating_profit', 'plan');
  return { opActual, opPlan, opDelta: opActual - opPlan, drivers, hasBaseline: !!baselineId };
}
